import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Link2, Loader2, RefreshCw } from "lucide-react";
import {
  CanonicalReviewDecisionDialog,
  type CanonicalReviewDecision,
} from "@/components/compliance-review/CanonicalReviewDecisionDialog";
import {
  CanonicalRelationshipDocumentReviewPanel,
} from "@/components/compliance-review/CanonicalRelationshipDocumentReviewPanel";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { Contractor } from "@/data/types";
import type {
  CanonicalRelationshipReviewDetail,
  CanonicalRelationshipReviewHistory,
  CanonicalRelationshipReviewQueue,
} from "@/data/contractor/compliance/canonicalRelationshipReview";
import {
  CANONICAL_INTERNAL_REVIEW_READ_ERROR_MESSAGES,
  CanonicalInternalReviewReadClientError,
} from "@/services/canonical-internal-review-read.shared";
import {
  createRelationshipReviewOpsReviewReadClient,
} from "@/services/relationship-review-ops-review.service";

type RelationshipReadClient = ReturnType<
  typeof createRelationshipReviewOpsReviewReadClient
>;

const defaultClient = createRelationshipReviewOpsReviewReadClient();

function errorMessage(error: unknown) {
  const kind = error instanceof CanonicalInternalReviewReadClientError
    ? error.kind
    : "unexpected_error";
  return CANONICAL_INTERNAL_REVIEW_READ_ERROR_MESSAGES[kind];
}

function safeDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function statusLabel(value: string): string {
  const normalized = value.replace(/_/g, " ");
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function prerequisitePresentation(blocker: string | null): Readonly<{
  summary: string;
  description: string;
}> {
  switch (blocker) {
    case null:
      return {
        summary: "Satisfied",
        description: "No document prerequisite blocker was reported.",
      };
    case "relationship_document_missing":
      return {
        summary: "Missing",
        description: "Missing required document",
      };
    case "relationship_document_review_pending":
      return {
        summary: "Pending",
        description: "Pending document review",
      };
    case "relationship_document_rejected":
      return {
        summary: "Rejected",
        description: "A required document was rejected",
      };
    case "relationship_document_changes_requested":
      return {
        summary: "Changes requested",
        description: "Changes requested for a required document",
      };
    case "relationship_document_expired":
      return {
        summary: "Expired",
        description: "A required document is expired",
      };
    default:
      return {
        summary: "Blocked",
        description: statusLabel(blocker),
      };
  }
}

function RelationshipStateSummary({
  reviewStatus,
  reviewDecision,
  prerequisiteBlocker,
  compact = false,
}: Readonly<{
  reviewStatus: string;
  reviewDecision: string;
  prerequisiteBlocker: string | null;
  compact?: boolean;
}>) {
  const prerequisite = prerequisitePresentation(prerequisiteBlocker);
  return (
    <dl
      className={compact
        ? "mt-2 grid gap-2 text-xs sm:grid-cols-3"
        : "mt-3 grid gap-3 rounded-md border bg-muted/20 p-3 text-xs sm:grid-cols-3"}
      aria-label="Relationship state summary"
    >
      <div>
        <dt className="font-medium">Workflow status</dt>
        <dd className="text-muted-foreground">{statusLabel(reviewStatus)}</dd>
      </div>
      <div>
        <dt className="font-medium">Aggregate decision</dt>
        <dd className="text-muted-foreground">{statusLabel(reviewDecision)}</dd>
      </div>
      <div>
        <dt className="font-medium">Document prerequisites</dt>
        <dd className="text-muted-foreground">{prerequisite.summary}</dd>
        <dd className="text-muted-foreground">{prerequisite.description}</dd>
      </div>
    </dl>
  );
}

export function CanonicalRelationshipReviewReadPanel({
  organizationId,
  parentContractorId,
  parentContractorLabel,
  contractors,
  contextKey,
  client = defaultClient,
}: Readonly<{
  organizationId: string;
  parentContractorId: string;
  parentContractorLabel: string;
  contractors: readonly Contractor[];
  contextKey: string;
  client?: RelationshipReadClient;
}>) {
  const generation = useRef(0);
  const [queue, setQueue] =
    useState<CanonicalRelationshipReviewQueue | null>(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState<unknown>(null);
  const [detail, setDetail] =
    useState<CanonicalRelationshipReviewDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<unknown>(null);
  const [history, setHistory] =
    useState<CanonicalRelationshipReviewHistory | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<unknown>(null);
  const [decisionDraft, setDecisionDraft] =
    useState<CanonicalReviewDecision | null>(null);
  const [changingDecision, setChangingDecision] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const contractorLabels = useMemo(
    () => new Map(contractors.map((contractor) => [
      contractor.id,
      String(contractor.companyName || "").trim(),
    ])),
    [contractors],
  );

  const loadQueue = useCallback(async () => {
    const requestGeneration = ++generation.current;
    setQueueLoading(true);
    setQueueError(null);
    try {
      const result = await client.listQueue({
        organizationId,
        parentContractorId,
        limit: 25,
      });
      if (generation.current === requestGeneration) setQueue(result);
    } catch (error) {
      if (generation.current === requestGeneration) {
        setQueue(null);
        setQueueError(error);
      }
    } finally {
      if (generation.current === requestGeneration) setQueueLoading(false);
    }
  }, [client, organizationId, parentContractorId]);

  useEffect(() => {
    generation.current += 1;
    setQueue(null);
    setQueueError(null);
    setDetail(null);
    setDetailLoading(false);
    setDetailError(null);
    setHistory(null);
    setHistoryLoading(false);
    setHistoryError(null);
    setDecisionDraft(null);
    setChangingDecision(false);
    setSuccessMessage("");
    void loadQueue();
    return () => {
      generation.current += 1;
    };
  }, [contextKey, loadQueue]);

  async function openDetail(reviewId: string): Promise<void> {
    if (detailLoading) return;
    const requestGeneration = ++generation.current;
    setDetailLoading(true);
    setDetailError(null);
    setHistory(null);
    setHistoryError(null);
    setDecisionDraft(null);
    setChangingDecision(false);
    try {
      const result = await client.getDetail({
        organizationId,
        parentContractorId,
        reviewId,
      });
      if (generation.current === requestGeneration) setDetail(result);
    } catch (error) {
      if (generation.current === requestGeneration) {
        setDetail(null);
        setDetailError(error);
      }
    } finally {
      if (generation.current === requestGeneration) setDetailLoading(false);
    }
  }

  async function loadHistory(reviewId: string): Promise<void> {
    if (historyLoading) return;
    const requestGeneration = ++generation.current;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const result = await client.getHistory({
        organizationId,
        parentContractorId,
        reviewId,
      });
      if (generation.current === requestGeneration) setHistory(result);
    } catch (error) {
      if (generation.current === requestGeneration) {
        setHistory(null);
        setHistoryError(error);
      }
    } finally {
      if (generation.current === requestGeneration) setHistoryLoading(false);
    }
  }

  async function submitDecision(input: Readonly<{
    reasonCode?: string;
    note?: string;
    idempotencyKey: string;
  }>): Promise<void> {
    if (!detail || !decisionDraft) return;
    const selectedReviewId = detail.reviewId;
    try {
      await client.submitDecision({
        organizationId,
        parentContractorId,
        reviewId: selectedReviewId,
        decision: decisionDraft,
        ...input,
        expectedLatestDecisionId: detail.latestDecisionId,
      });
      setDecisionDraft(null);
      setChangingDecision(false);
      setSuccessMessage("Relationship review decision recorded.");
      await loadQueue();
      await openDetail(selectedReviewId);
      await loadHistory(selectedReviewId);
    } catch (error) {
      if (
        error instanceof CanonicalInternalReviewReadClientError
        && (
          error.kind === "stale_decision"
          || error.kind === "idempotency_conflict"
        )
      ) {
        await loadQueue();
        await openDetail(selectedReviewId);
        await loadHistory(selectedReviewId);
      }
      throw error;
    }
  }

  return (
    <Card aria-label="Relationship Review">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Link2 className="h-4 w-4" />
              Relationship Review
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Parent-to-child governance reviews under {parentContractorLabel}.
              These decisions do not approve company or personal evidence.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={queueLoading}
            onClick={() => void loadQueue()}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {queueLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading relationship review queue…
          </div>
        ) : null}
        {queueError ? (
          <Alert variant="destructive" role="alert">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{errorMessage(queueError).title}</AlertTitle>
            <AlertDescription>
              {errorMessage(queueError).description}
            </AlertDescription>
          </Alert>
        ) : null}
        {successMessage ? (
          <Alert aria-live="polite">
            <AlertTitle>Decision recorded</AlertTitle>
            <AlertDescription>{successMessage}</AlertDescription>
          </Alert>
        ) : null}
        {!queueLoading && !queueError && queue ? (
          <p className="text-sm font-medium">
            Relationships requiring review ({queue.items.length})
          </p>
        ) : null}
        {!queueLoading && !queueError && queue?.items.length === 0 ? (
          <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            No parent-to-child relationships are available in this scope.
          </div>
        ) : null}

        {queue?.items.map((item) => {
          const childLabel = contractorLabels.get(item.childContractorId) ?? "";
          const resolved = Boolean(childLabel);
          return (
            <div key={item.reviewId} className="rounded-md border p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-medium">
                    {parentContractorLabel} →{" "}
                    {resolved ? childLabel : "Company identity unavailable"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {item.requiredCount} requirements ·{" "}
                    {item.activeDocumentCount} active documents
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!resolved || detailLoading}
                  onClick={() => void openDetail(item.reviewId)}
                >
                  Open relationship detail
                </Button>
              </div>
              <RelationshipStateSummary
                reviewStatus={item.reviewStatus}
                reviewDecision={item.reviewDecision}
                prerequisiteBlocker={item.prerequisiteBlocker}
                compact
              />
              {!resolved ? (
                <p className="mt-2 text-xs text-destructive">
                  The child contractor is not present in the trusted onboarding
                  context. Detail and history reads are disabled.
                </p>
              ) : null}
            </div>
          );
        })}

        {detailLoading ? (
          <p className="text-sm text-muted-foreground">
            Loading relationship detail…
          </p>
        ) : null}
        {detailError ? (
          <Alert variant="destructive" role="alert">
            <AlertTitle>{errorMessage(detailError).title}</AlertTitle>
            <AlertDescription>
              {errorMessage(detailError).description}
            </AlertDescription>
          </Alert>
        ) : null}
        {detail ? (
          <div className="rounded-md border p-3" aria-label="Relationship review detail">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold">
                  {parentContractorLabel} →{" "}
                  {contractorLabels.get(detail.childContractorId)
                    ?? "Company identity unavailable"}
                </h3>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={historyLoading}
                onClick={() => void loadHistory(detail.reviewId)}
              >
                View immutable history
              </Button>
            </div>
            <RelationshipStateSummary
              reviewStatus={detail.reviewStatus}
              reviewDecision={detail.reviewDecision}
              prerequisiteBlocker={detail.prerequisiteBlocker}
            />
            {detail.reviewDecision !== "pending" && !changingDecision ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge>
                  Current decision:{" "}
                  {statusLabel(detail.reviewDecision)}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setChangingDecision(true)}
                >
                  Change relationship decision
                </Button>
              </div>
            ) : (
              <div
                className="mt-3 flex flex-wrap gap-2"
                aria-label="Relationship review actions"
              >
                {detail.reviewDecision !== "approved" ? (
                  <Button
                    size="sm"
                    disabled={Boolean(detail.prerequisiteBlocker)}
                    onClick={() => setDecisionDraft("approved")}
                  >
                    Approve relationship
                  </Button>
                ) : null}
                {detail.reviewDecision !== "rejected" ? (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setDecisionDraft("rejected")}
                  >
                    Reject relationship
                  </Button>
                ) : null}
                {detail.reviewDecision !== "changes_requested" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDecisionDraft("changes_requested")}
                  >
                    Request changes
                  </Button>
                ) : null}
                {detail.reviewDecision !== "pending" ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setChangingDecision(false)}
                  >
                    Cancel decision change
                  </Button>
                ) : null}
              </div>
            )}
            {detail.prerequisiteBlocker ? (
              <p className="mt-2 text-xs text-warning">
                Approval is unavailable until this prerequisite is resolved:{" "}
                {prerequisitePresentation(detail.prerequisiteBlocker).description}.
              </p>
            ) : null}
            <CanonicalRelationshipDocumentReviewPanel
              organizationId={organizationId}
              parentContractorId={parentContractorId}
              relationshipDetail={detail}
              relationshipLabel={`${parentContractorLabel} → ${
                contractorLabels.get(detail.childContractorId)
                  ?? "Company identity unavailable"
              }`}
              client={client}
              onDocumentDecisionCommitted={async () => {
                await loadQueue();
                await openDetail(detail.reviewId);
              }}
            />
          </div>
        ) : null}

        {historyError ? (
          <Alert variant="destructive" role="alert">
            <AlertTitle>{errorMessage(historyError).title}</AlertTitle>
            <AlertDescription>
              {errorMessage(historyError).description}
            </AlertDescription>
          </Alert>
        ) : null}
        {history ? (
          <div className="rounded-md border p-3" aria-label="Relationship decision history">
            <h3 className="font-semibold">Immutable decision history</h3>
            {history.decisions.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">
                No relationship decisions recorded.
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                {history.decisions.map((decision) => (
                  <div key={decision.decisionId} className="rounded border p-2 text-xs">
                    <p className="font-medium">
                      {decision.decision.replace(/_/g, " ")}
                    </p>
                    <p className="text-muted-foreground">
                      {safeDate(decision.decidedAt)} · {decision.reviewerRole}
                    </p>
                    {decision.reasonCode ? (
                      <p>Reason: {decision.reasonCode.replace(/_/g, " ")}</p>
                    ) : null}
                    {decision.note ? (
                      <p className="whitespace-pre-wrap">{decision.note}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}

        <p className="text-xs text-muted-foreground">
          Relationship decisions govern only the selected parent-to-child
          relationship. They do not approve company or personal evidence.
        </p>
      </CardContent>
      <CanonicalReviewDecisionDialog
        decision={decisionDraft}
        targetLabel={`${parentContractorLabel} → ${
          detail
            ? contractorLabels.get(detail.childContractorId)
              ?? "Company identity unavailable"
            : "selected relationship"
        }`}
        onClose={() => setDecisionDraft(null)}
        onConfirm={submitDecision}
      />
    </Card>
  );
}
